<script setup lang="ts">
import { cn } from '@/utils/utils'
import { useEventListener, useMediaQuery, useVModel } from '@vueuse/core'
import { computed, ref, watch, type HTMLAttributes, type Ref } from 'vue'
import { provideSidebarContext, SIDEBAR_KEYBOARD_SHORTCUT, SIDEBAR_WIDTH_ICON } from './utils'
import { useSidebarStore } from '@/stores/sidebarStore'

// `$attrs` are bound on the wrapper div by hand, so they must not also fall through to it.
defineOptions({ inheritAttrs: false })

const props = withDefaults(defineProps<{
  defaultOpen?: boolean
  open?: boolean
  class?: HTMLAttributes['class']
  /**
   * Width the sidebar keeps when collapsed. The app frame passes its icon
   * strip's width, so collapsing hides the section column and leaves the strip.
   */
  iconWidth?: string
  /**
   * Fixed width added to the resizable width, for a column that never resizes.
   * The app frame passes its icon strip's width here too.
   */
  reservedWidthPx?: number
}>(), {
  defaultOpen: true,
  open: undefined,
  iconWidth: SIDEBAR_WIDTH_ICON,
  reservedWidthPx: 0,
})

const emits = defineEmits<{
  'update:open': [open: boolean]
}>()

// Below Tailwind's `md` (768px), to match every `md:` class in the app. At
// `max-width: 768px` a 768px screen counted as a phone here while `md:` already
// treated it as a tablet: the sidebar became the hidden phone sheet, and the
// phone's bottom tab bar (`md:hidden`) was hidden too, so nothing navigated.
const isMobile = useMediaQuery('(max-width: 767.98px)')
const openMobile = ref(false)
const sidebarStore = useSidebarStore()

// Use Pinia store for uncontrolled mode, props for controlled mode
const open = useVModel(props, 'open', emits, {
  defaultValue: props.open !== undefined ? props.open : sidebarStore.open,
  passive: (props.open === undefined) as false,
}) as Ref<boolean>

// Sync store with v-model when not controlled (only watch store -> v-model to avoid circular updates)
if (props.open === undefined) {
  watch(() => sidebarStore.open, (value) => {
    if (open.value !== value) {
      open.value = value
    }
  }, { immediate: true })
}

function setOpen(value: boolean) {
  open.value = value // emits('update:open', value)
  
  // Update store if not controlled
  if (props.open === undefined) {
    sidebarStore.setOpen(value)
  }
}

function setOpenMobile(value: boolean) {
  openMobile.value = value
}

// Helper to toggle the sidebar.
function toggleSidebar() {
  return isMobile.value ? setOpenMobile(!openMobile.value) : setOpen(!open.value)
}

useEventListener('keydown', (event: KeyboardEvent) => {
  if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
    event.preventDefault()
    toggleSidebar()
  }
})

// Sidebar width is store-owned (persisted to localStorage so the user's
// chosen rail position survives reloads). The provider reads it as a ref
// so consumers downstream can react without re-querying Pinia themselves.
const width = computed({
  get: () => sidebarStore.width,
  set: (value: number) => {
    sidebarStore.setWidth(value)
  },
}) as unknown as Ref<number>

function setWidth(value: number): void {
  sidebarStore.setWidth(value)
}

// Drag-state lives in the provider so the wrapper can suppress the width
// transition while the rail is being dragged (avoids easing lag) and so
// `Sidebar.vue` can disable text selection on the page during the drag.
const isResizing = ref(false)
function setResizing(value: boolean): void {
  isResizing.value = value
}

// We add a state so that we can do data-state="expanded" or "collapsed".
// This makes it easier to style the sidebar with Tailwind classes.
const state = computed(() => open.value ? 'expanded' : 'collapsed')

provideSidebarContext({
  state,
  open,
  setOpen,
  isMobile,
  openMobile,
  setOpenMobile,
  toggleSidebar,
  width,
  setWidth,
  isResizing,
  setResizing,
})
</script>

<template>
  <div
    data-slot="sidebar-wrapper"
    :data-resizing="isResizing ? '' : undefined"
    :style="{
      '--sidebar-width': `${width + props.reservedWidthPx}px`,
      '--sidebar-width-icon': props.iconWidth,
    }"
    :class="cn('flex h-full w-full group/sidebar-wrapper has-data-[variant=inset]:bg-sidebar', props.class)"
    v-bind="$attrs"
  >
    <slot />
  </div>
</template>
